import React, { useCallback, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooterButtons,
  ModalCloseButton,
} from '@televet/kibble-ui/build/components/Modal';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useValidation } from 'shared/hooks';

interface ReceiptEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  sendReceiptEmail: (emailOverride?: string) => Promise<void>;
  isSending: boolean;
}

const ReceiptEmailModal = ({
  isOpen,
  onClose,
  sendReceiptEmail,
  isSending,
}: ReceiptEmailModalProps): JSX.Element | null => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { validateEmail } = useValidation();
  const [isInvalid, setIsInvalid] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (validateEmail(inputRef.current?.value || '').isValid) {
      setIsInvalid(false);
      await sendReceiptEmail(inputRef.current?.value || '');
      onClose();
    } else {
      setIsInvalid(true);
    }
  }, [validateEmail, sendReceiptEmail, onClose]);

  const handleEnterKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return isOpen ? (
    <Modal size="sm" isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Missing Email Address</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <TextInput
          autoFocus
          ref={inputRef}
          type="email"
          placeholder="name@example.com"
          label="Enter email address:"
          onSubmit={handleSubmit}
          onKeyDown={handleEnterKey}
          isInvalid={isInvalid}
          errorText="Please enter a valid email address."
        />
      </ModalBody>
      <ModalFooterButtons
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitText="Send Receipt"
        isLoading={isSending}
        isDisabled={isSending}
      />
    </Modal>
  ) : null;
};

export default ReceiptEmailModal;
